import queryString from "./queryString";
import routes from "./cacaRoutes";
import raw from "./rawClients/rawCacaClient";
import getAllFeatureFlagsSerializer from "./serializers/getAllFeatureFlags";
import documentSerializer from "./serializers/document";
import markupSerializer from "./serializers/markup";
import issueSerializer from "./serializers/issues";
/**
 * Cacahuete Endpoints
 *
 * Cacahuete documentation here:
 *   http://docs.planfront.net/docs/cacahuete/en/latest/
 *   https://github.com/plangrid/cacahuete
 *
 * When adding endpoints:
 *   - Follow the nesting in cacaRoutes as closely as possible
 *   - Use a semantic verb as the first word in the method. This doesn't need to match the HTTP verb
 *     exactly, but GET should map to `get` or `getAll`.
 *   - Method arguments should be listed in this order:
 *      - url parameters (eg, `workspaceUid` in `/workspaces/${workspaceUid}`
 *      - query parameters object (eg, `{ updatedAt: updated_at }` in `/projects?updated_at=`
 *      - body object or body arguments
 *   - Don't include the version number in the method name or nesting, unless there is ambiguity
 */
/**
 * Encodes an array as multiple cacahuete params
 *
 * @example
 *   arrayToCacaParams("fields", ["uid", "name"])
 *   >  "fields=uid&fields=name"
 *
 * @param param
 * @param values
 */
const arrayToCacaParams = (param, values) =>
  values.map(val => `${param}=${encodeURIComponent(val)}`).join("&");
const uuid = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
const getInit = userId => ({ headers: new Headers({ "X-PG-UserId": userId }) });
const DocumentType = {
  FOLDER: "folder",
  FILE: "file",
};
const documents = {
  createFolderInProject: (projectUid, data) =>
    raw
      .post(routes.documents.oneInProject(projectUid, uuid()), {
        ...data,
        document_type: DocumentType.FOLDER,
        file_type: DocumentType.FOLDER,
        key: uuid(),
      })
      .then(documentSerializer.fromServer),
  createDocument: (projectUid, documentUid, params, secure = false) => {
    if (!secure) {
      // tslint:disable-next-line:no-console
      console.warn(
        "DEPRECATION WARNING: Calling documents.createDocument without secure flag will be deprecated soon. Pass key_signature and pass secure true."
      );
    }
    const route = secure
      ? routes.documents.oneInProjectSecure(projectUid, documentUid)
      : routes.documents.oneInProject(projectUid, documentUid);
    return raw.post(route, params);
  },
  deleteInProject: (projectUid, documentUid) =>
    raw.delete(routes.documents.oneInProject(projectUid, documentUid)),
  getAllInProject: (projectUid, params = {}) =>
    raw.get({
      url: routes.documents.allInProject(projectUid),
      params,
    }),
  getOneInProject: (projectUid, documentUid, params = {}) =>
    raw.get({
      url: routes.documents.oneInProject(projectUid, documentUid),
      params,
    }),
  uploadAssetInProject: (
    projectUid,
    documentUid,
    fileSize,
    fileExtension,
    contentType,
    secure = false
  ) => {
    if (!secure) {
      // tslint:disable-next-line:no-console
      console.warn(
        "DEPRECATION WARNING: Calling documents.uploadAssetInProject without secure flag will be deprecated soon. Pass secure true and pass key_signature to createDocument."
      );
    }
    const route = secure
      ? routes.attachments.oneAssetInProjectSecure(projectUid, documentUid)
      : routes.attachments.oneAssetInProject(projectUid, documentUid);
    return raw.post(route, {
      attachment_size: fileSize,
      file_type: fileExtension,
      content_type: contentType,
    });
  },
  getOrCreateFolderByName: (projectUid, name) =>
    documents
      .getAllInProject(projectUid, {
        name,
        document_type: DocumentType.FOLDER,
      })
      .then(({ documents: documentsFromServer }) => {
        const _documents = documentsFromServer.map(
          documentSerializer.fromServer
        );
        const doc = _documents.find(doc => doc.name === name);
        return doc === undefined
          ? documents.createFolderInProject(projectUid, { name })
          : doc;
      }),
};
const staples = {
  getStaple: (projectUid, stapleUid) =>
    raw.get(routes.staples.one(projectUid, stapleUid)),
  createSheetsReport: (projectUid, sheetUids, params) =>
    raw
      .post(routes.staples.all(projectUid), {
        type: "staple",
        sheets: sheetUids,
        annotations: params.annotations || null,
        inc_user_annotations: params.userAnnotations || false,
        inc_master: params.masterAnnotations || false,
      })
      .then(({ uid }) => staples.getStaple(projectUid, uid)),
  createStaple: (projectUid, data) =>
    raw
      .post(routes.staples.all(projectUid), data)
      .then(({ uid }) => staples.getStaple(projectUid, uid)),
  getStaples: (projectUid, { type, fields, ...params }) => {
    const typeParam = arrayToCacaParams("type", type);
    const fieldsParam = arrayToCacaParams("fields", fields);
    return raw.get({
      url: `${routes.staples.all(projectUid)}?${typeParam}&${fieldsParam}`,
      params,
    });
  },
};
const resources = {
  // Three resource types are available (photos|snapshots|attachments)
  getAllInProject: (projectUid, resourceType, uids, params) =>
    raw.post(routes.resources.allInProject(projectUid, resourceType), {
      ...params,
      uids,
    }),
};
const photos = {
  createOneInProject: (projectUid, photoUid, params) =>
    raw.put(routes.photos.oneInProject(projectUid, photoUid), params),
  getOneInProject: (projectUid, photoUid) =>
    raw.get(routes.photos.oneInProject(projectUid, photoUid)),
  getAllInMarkup: (projectUid, markupUid, totalCount) =>
    photos.getAllInProject(projectUid, { annotation: markupUid }, totalCount),
  getAllInPunch: (projectUid, punchUid, totalCount) =>
    photos.getAllInProject(projectUid, { punch: punchUid }, totalCount),
  getAllInSheet: (projectUid, sheetUid, totalCount) =>
    photos.getAllInProject(projectUid, { sheet: sheetUid }, totalCount),
  getAllInProject: (projectUid, params, totalCount = 300) =>
    raw.getAll(
      routes.photos.allInProject(projectUid),
      { totalCount },
      { method: "POST" },
      params
    ),
  getAllInProjectMeta: (projectUid, params = {}) =>
    raw.post(routes.photos.allInProjectMeta(projectUid), params),
  updateInProject: (projectUid, photoUid, params) =>
    raw.put(routes.photos.oneInProject(projectUid, photoUid), params),
  uploadAssetInProject: (
    projectUid,
    photoUid,
    fileType,
    fileSize,
    imageType = "normal"
  ) =>
    raw.post(routes.photos.oneAssetInProject(projectUid, photoUid, fileType), {
      filesize: fileSize,
      image_type: imageType,
    }),
  deleteInProject: (projectUid, photoUid, params) =>
    raw.delete({
      url: routes.photos.oneInProject(projectUid, photoUid),
      params,
    }),
  getByPhotoUids: (projectUid, photoUids) =>
    photos.getAllInProject(
      projectUid,
      { photo_uids: photoUids },
      photoUids.length
    ),
  getOneShareableInProject: (projectUid, photoUid) =>
    raw.get(routes.photos.oneShareableInProject(projectUid, photoUid)),
};
export default {
  NAME: "cacaClient",
  comments: {
    getAllInProject: projectUid => raw.get(routes.comments.all(projectUid)),
    getAllInThread: (type, projectUid, targetUid) =>
      raw.get(routes.comments.all(projectUid, type, targetUid)),
    put: (projectUid, draft, type, targetUid) => {
      const data = {
        uid: uuid(),
        text: draft,
        user_created_at: { $date: Date.now() },
      };
      return raw.put(
        routes.comments.one(projectUid, data.uid, type, targetUid),
        data
      );
    },
  },
  documents,
  email: {
    send: params => raw.post(routes.email.send(), params),
  },
  fieldReports: {
    getFieldReport: (projectId, reportTypeId, reportId) =>
      raw.get(
        routes.fieldReports.oneInProjectForType(
          projectId,
          reportTypeId,
          reportId
        )
      ),
    getFieldReports: (projectUid, reportTypeUid, params) =>
      raw.get({
        url: routes.fieldReports.allInProjectForType(projectUid, reportTypeUid),
        params,
      }),
    updateFieldReport: (projectId, reportTypeId, reportId, params) =>
      raw.put(
        routes.fieldReports.oneInProjectForType(
          projectId,
          reportTypeId,
          reportId
        ),
        params
      ),
    getFieldReportFile: (projectId, fileId, params) =>
      raw.get({
        url: routes.fieldReportFiles.oneInProject(projectId, fileId),
        params,
      }),
    createFieldReportFile: (projectId, params) =>
      raw.post(routes.fieldReportFiles.allInProject(projectId), params),
    createFieldReportTemplate: (projectId, fileId, params) =>
      raw.post(
        routes.fieldReportTemplates.oneInProject(projectId, fileId),
        params
      ),
    createFieldReportType: (projectId, reportTypeId, params) =>
      raw.post(
        routes.fieldReportTypes.oneInProject(projectId, reportTypeId),
        params
      ),
    getFieldReportTypes: (projectId, params) =>
      raw.get({
        url: routes.fieldReportTypes.allInProject(projectId),
        params,
      }),
    getFieldReportType: (projectId, reportTypeId, params) =>
      raw.get({
        url: routes.fieldReportTypes.oneInProject(projectId, reportTypeId),
        params,
      }),
    updateFieldReportType: (projectId, reportTypeId, params) =>
      raw.put(
        routes.fieldReportTypes.oneInProject(projectId, reportTypeId),
        params
      ),
  },
  featureFlags: {
    getAll: (projectUid, params = {}) =>
      raw
        .get(
          [
            routes.featureFlags.all(),
            queryString.stringify({
              include_organization_flags: true,
              flags: [
                "submittals_override",
                "spec_extraction_for_organizations",
                "submittals",
                "submittals_for_organizations",
                "workspaces",
                ...(params.flags || []),
              ],
              merge_organization_skus_for_project: projectUid,
            }),
          ].join("?")
        )
        .then(getAllFeatureFlagsSerializer),
  },
  issues: {
    create: (projectUid, issueUid, body) =>
      raw.post(routes.issues.oneInProject(projectUid, issueUid), {
        uid: issueUid,
        ...body,
      }),
    getOneInProject: (projectUid, issueUid) =>
      raw.get(routes.issues.oneInProject(projectUid, issueUid)),
    updateOneInProject: (projectUid, issueUid, issue) =>
      raw.put(
        routes.issues.oneInProject(projectUid, issueUid),
        issueSerializer.toServer(issue)
      ),
    updateBatchInProject: (projectUid, issueUids, updates) =>
      raw.put(routes.issues.batchInProject(projectUid), {
        updates,
        issues_uids: issueUids,
      }),
    getAllDefaultsInProject: projectUid =>
      raw.get(routes.issues.allDefaultsInProject(projectUid)),
    deleteInProject: (projectUid, issueUids) =>
      raw.delete(routes.issues.allInProject(projectUid), issueUids),
    getAllListsInProject: projectUid =>
      raw.get(routes.issues.allListsInProject(projectUid)),
    getAllMetaInProject: projectUid =>
      raw.get(routes.issues.allMetaInProject(projectUid)),
    filterAllInProject: (projectUid, params) =>
      raw.post(routes.issues.filterAllInProject(projectUid), params),
    getStatsInProject: (projectUid, params) =>
      raw.get(
        `${routes.punchList.statsForProject(projectUid)}${
          params ? `?${queryString.stringify(params)}` : ""
        }`
      ),
  },
  issueLists: {
    create: (projectUid, issueListUid, body) =>
      raw.post(routes.issueLists.oneInProject(projectUid, issueListUid), {
        uid: issueListUid,
        ...body,
      }),
    getOneInProject: (projectUid, issueListUid) =>
      raw.get(routes.issueLists.oneInProject(projectUid, issueListUid)),
    updateOneInProject: (projectUid, issueListUid, issueList) =>
      raw.put(
        routes.issueLists.oneInProject(projectUid, issueListUid),
        issueList
      ),
    deleteInProject: (projectUid, issueListUid) =>
      raw.delete(routes.issueLists.oneInProject(projectUid, issueListUid)),
  },
  issueUploads: {
    getSignedS3Data: (projectUid, uploadUid, params) =>
      raw.post(routes.issueUploads.asset(projectUid, uploadUid), params),
    createIssueUploadJob: (projectUid, uploadUid, params) =>
      raw.put(routes.issueUploads.oneInProject(projectUid, uploadUid), params),
    checkOnIssueUploadJob: (projectUid, uploadUid) =>
      raw.get(routes.issueUploads.oneInProject(projectUid, uploadUid)),
  },
  // Cacahuete calls these "annotations", but we have standardized on "markups" in PlanWeb
  markups: {
    getAllInSheet: (projectUid, sheetUid, params) =>
      raw
        .get({
          url: routes.markups.allInProjectSheet(projectUid, sheetUid),
          params,
        })
        .then(markupSerializer.fromServer),
    updateInSheet: (projectUid, sheetUid, markups) =>
      raw.put(routes.markups.allInProjectSheet(projectUid, sheetUid), markups),
    createInSheet: (projectUid, sheetUid, markup) =>
      raw.post(routes.markups.allInProjectSheet(projectUid, sheetUid), markup),
    createInProject: (projectUid, issueUid, markup) =>
      raw.post(routes.markups.allInProject(projectUid), {
        punch: issueUid,
        ...markup,
      }),
    // there is no endpoint to create stamps with sheetId, so we must use project
    createStampInProject: (projectUid, stamp, issue) =>
      raw
        .post(routes.markups.stampInProject(projectUid), {
          annotation: stamp,
          punch: issue,
        })
        .then(data => issueSerializer.createStampFromServer(data)),
    deleteInSheet: (projectUid, sheetUid, markupUids) =>
      raw.delete(
        routes.markups.allInProjectSheet(projectUid, sheetUid),
        markupUids
      ),
  },
  messageAcknowledgments: {
    acknowledge: (name, value) =>
      raw.post(routes.messageAcknowledgments.one(), { name, value }),
  },
  photos,
  profile: {
    update: (projectUid, data) => raw.put(routes.profile.one(projectUid), data),
  },
  projects: {
    getOne: projectUid => raw.get(routes.projects.one(projectUid)),
    getAll: params =>
      raw.get({
        url: routes.projects.all(),
        params,
      }),
    delete: (projectUid, userId) =>
      raw.delete(routes.projects.one(projectUid), getInit(userId)),
    addUser: (projectUid, data) =>
      raw.post(routes.projects.users(projectUid), data),
    removeUser: (projectUid, data) =>
      raw.delete(routes.projects.users(projectUid), data),
  },
  rfis: {
    getAllInProject: (projectUid, params) =>
      raw.get({
        url: routes.rfis.allInProject(projectUid),
        params,
      }),
    getAllLabelsInProject: projectUid =>
      raw.get(routes.rfis.allLabelsInProject(projectUid)),
  },
  sheetReferenceData: {
    // Contains tags, unique sheet names, and unique version names
    // See: http://docs.planfront.net/docs/cacahuete/en/latest/sheets.html?highlight=sheets_ref#get--v2-project-(uid-project)-sheets_ref
    getFromProject: (projectUid, params) =>
      raw.get({
        url: routes.sheets.referenceDataInProject(projectUid),
        params,
      }),
  },
  sheets: {
    getOneInProject: (projectUid, sheetUid) =>
      raw.get(routes.sheets.oneInProject(projectUid, sheetUid)),
    getAllSheetsInProject: (projectUid, params) =>
      raw.get({
        url: routes.sheets.allInProject(projectUid),
        params,
      }),
    getWithFilterInProject: (projectUid, filterParams) =>
      raw.post(routes.sheets.filterInProject(projectUid), filterParams),
    // Get all sheets in a project. `fields` is an array of fields you want. `filterStatus` lets you
    // specify which status to fetch (defaults to "fin", which means published). *Requires* an
    // expected number of sheets in total, which you can get from the Project object.
    getAllInProject: (
      projectUid,
      fields,
      sheetCount,
      filterStatus = "fin",
      onProgress = () => {}
    ) =>
      raw.getAll(
        `${routes.sheets.pageInProject(projectUid)}?${arrayToCacaParams(
          "fields",
          fields
        )}&status=${filterStatus}`,
        { totalCount: sheetCount, perPage: 3000, onProgress }
      ),
    getMetaInProject: projectUid =>
      raw.get(routes.sheets.metaInProject(projectUid)),
    updateOneInProject: (projectUid, sheetData) =>
      raw.put(routes.sheets.pageInProject(projectUid), [sheetData]),
    delete: (projectUid, sheetUids) =>
      raw.delete(routes.sheets.pageInProject(projectUid), sheetUids),
  },
  snapshots: {
    getAllInProject: (projectUid, params = {}) =>
      raw.get({
        url: routes.snapshots.allInProject(projectUid),
        params,
      }),
    uploadAssetInProject: (projectUid, snapshotUid, params) =>
      raw.post(routes.snapshots.oneInProject(projectUid, snapshotUid), params),
    updateOneInProject: (projectUid, snapshotUid, params) =>
      raw.put(routes.snapshots.oneInProject(projectUid, snapshotUid), params),
    emailInProject: (projectUid, params) =>
      raw.post(routes.snapshots.emailInProject(projectUid), params),
  },
  staples,
  users: {
    createNewUser: (firstName, lastName, email, password, withProject = true) =>
      raw.post(routes.users.users(), {
        first_name: firstName,
        last_name: lastName,
        email,
        password,
        lang: "en",
        signup_via: "web",
        with_project: withProject,
      }),
    emailAvailability: email =>
      raw.get({
        url: routes.users.emailAvailability(),
        params: { email },
      }),
    getAllInProject: (projectUid, params) =>
      raw.get({
        url: routes.users.projectUsers(projectUid),
        params,
      }),
    addToProject: (projectUid, email, roleId) =>
      raw.post(routes.users.projectUsers(projectUid), {
        email: email,
        role: roleId,
      }),
    deleteSelf: pass =>
      raw.post(routes.users.deletion(), {
        password: pass,
      }),
    getUser: () =>
      raw.get({
        url: routes.users.users(),
      }),
    sendResetPasswordLink: email =>
      raw.post(routes.users.resetPassword(), { email }),
    getForgeMapping: (projectUid, params) =>
      raw.get({
        url: routes.users.forgeMapping(projectUid),
        params,
      }),
  },
  resources,
  orgProjects: {
    getAll: (orgId, userId, include_profiles) =>
      raw
        .getAll(
          {
            url: routes.orgProjects.all(orgId),
            params: { include_profiles },
          },
          {
            getPageFromResponse: response => response.items,
            getTotalCountFromResponse: response => response.total,
            perPage: 500,
          },
          getInit(userId)
        )
        .then(pages =>
          pages.reduce((projects, page) => projects.concat(page), [])
        ),
  },
  tags: {
    getAllInProject: projectUid => {
      return raw.get({
        url: routes.sheetTags.allInProject(projectUid),
      });
    },
  },
  versionSets: {
    getAllInProject: projectUid =>
      raw.get({ url: routes.versionSets.allInProject(projectUid) }),
  },
};
